import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Providers } from '@app/shared/interfaces/accounting-report.interface';
import { AccountingReportService } from '@app/shared/services/accounting-report-service.service';
import { ButtonModule } from 'primeng/button';
import { InputNumber } from 'primeng/inputnumber';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { ToggleSwitch } from 'primeng/toggleswitch';

export type AncillaryMarkupDialogMode = 'add' | 'edit';

export interface AncillaryMarkupFormModel {
    id: number | null;
    name: string;
    providerId: number | null;
    price: number;
    active: boolean;
}

@Component({
    selector: 'app-ancillary-markup-create-edit',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, InputText, InputNumber, ToggleSwitch, Select, ButtonModule],
    templateUrl: './ancillary-markup-create-edit.component.html'
})
export class AncillaryMarkupCreateEditComponent implements OnInit, OnChanges {
    @Input() mode: AncillaryMarkupDialogMode = 'add';
    @Input() data: Partial<AncillaryMarkupFormModel> | null = null;

    @Output() cancel = new EventEmitter<void>();
    @Output() save = new EventEmitter<AncillaryMarkupFormModel>();

    providers: Providers[] = [];
    submitted = false;

    private fb = inject(FormBuilder);
    private accountingReportService = inject(AccountingReportService);

    formGroup: FormGroup = this.fb.group({
        id: [null],
        name: ['', [Validators.required, Validators.maxLength(255)]],
        providerId: [null],
        price: [0, [Validators.required, Validators.min(0)]],
        active: [true]
    });

    ngOnInit(): void {
        this.loadLookups();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['data']) {
            if (this.data) {
                this.formGroup.patchValue({
                    id: this.data.id ?? null,
                    name: this.data.name ?? '',
                    providerId: this.data.providerId ?? null,
                    price: this.data.price ?? 0,
                    active: this.data.active ?? true
                });
            } else {
                this.formGroup.reset({
                    id: null,
                    name: '',
                    providerId: null,
                    price: 0,
                    active: true
                });
            }
            this.submitted = false;
        }
    }

    onCancel(): void {
        this.cancel.emit();
    }

    onSave(): void {
        this.submitted = true;
        if (this.formGroup.invalid) {
            this.formGroup.markAllAsTouched();
            return;
        }

        const v = this.formGroup.getRawValue() as any;
        const form: AncillaryMarkupFormModel = {
            id: v.id,
            name: v.name,
            providerId: v.providerId,
            price: v.price,
            active: !!v.active
        };

        this.save.emit(form);
    }

    private loadLookups(): void {
        this.accountingReportService.getProviders().subscribe({
            next: (res) => (this.providers = res || []),
            error: () => (this.providers = [])
        });
    }
}
